"use client";

import { useCallback, useEffect, useState } from "react";
import { apiErrorMessage } from "../lib/apiError";

type QueryState<T> = {
  data: T | null;
  /** True only before the first successful response (full-page spinner). */
  loading: boolean;
  /** True whenever a request is in flight. */
  isFetching: boolean;
  error: string | null;
  /** Re-run the query — call after a mutation changes the underlying data. */
  refetch: () => Promise<void>;
};

/**
 * Loads a single value from a generated SDK call, discarding results from
 * requests that a newer one has already superseded.
 *
 * The paginated sibling is `usePaginatedQuery`. Unlike that one, this checks
 * the SDK's `error` field as well as thrown exceptions, since hey-api returns
 * HTTP failures rather than throwing them.
 */
export function useQuery<T>(
  fetcher: () => Promise<{ data?: T; error?: unknown }>,
  errorMessage: string,
  deps: unknown[] = [],
  {
    enabled = true,
  }: {
    /**
     * When false, nothing is fetched (e.g. a heavy map layer that's switched
     * off). The query runs as soon as this turns true.
     */
    enabled?: boolean;
  } = {},
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Bumped by refetch; the effect below keys off it.
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setIsFetching(false);
      return;
    }
    let active = true;
    setIsFetching(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!active) return;
        if (result.error !== undefined || result.data === undefined) {
          setError(apiErrorMessage(result.error, errorMessage));
          return;
        }
        setData(result.data);
        setHasLoaded(true);
      })
      .catch(() => {
        if (active) setError(errorMessage);
      })
      .finally(() => {
        if (active) setIsFetching(false);
      });

    return () => {
      active = false;
    };
    // The fetcher closes over `deps`; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadCount, enabled]);

  const refetch = useCallback(async () => {
    setReloadCount((count) => count + 1);
  }, []);

  return {
    data,
    loading: !hasLoaded && isFetching,
    isFetching,
    error,
    refetch,
  };
}
